import { useEffect, useRef } from 'react';
import './RoomBackdrop.css';

/** Desktop / tablet landscape plates */
const ROOM_SRC_DESKTOP = {
  home: '/rooms/home.png?v=lobby2',
  services: '/rooms/services.png?v=lobby2',
  lead: '/rooms/lead.png?v=lobby2',
  transform: '/rooms/transform.png?v=lobby2',
  govern: '/rooms/govern.png?v=lobby2',
  work: '/rooms/work.png?v=lobby2',
  about: '/rooms/about.png?v=lobby2',
  partners: '/rooms/partners.png?v=lobby2',
  contact: '/rooms/contact.png?v=lobby2',
};

/**
 * Phone portrait plates — selected ≤720px via <picture> when READY.
 * Drop files at these paths and flip the matching key in MOBILE_PLATES_READY.
 */
const ROOM_SRC_MOBILE = {
  home: '/rooms/home-9x16.png',
  services: '/rooms/services-9x16.png',
  lead: '/rooms/lead-9x16.png',
  transform: '/rooms/transform-9x16.png',
  govern: '/rooms/govern-9x16.png',
  work: '/rooms/work-9x16.png',
  about: '/rooms/about-9x16.png',
  partners: '/rooms/partners-9x16.png',
  contact: '/rooms/contact-9x16.png',
};

/** Flip to true once the matching *-9x16.png exists under public/rooms/ */
const MOBILE_PLATES_READY = {
  home: false,
  services: false,
  lead: false,
  transform: false,
  govern: false,
  work: false,
  about: false,
  partners: false,
  contact: false,
};

const MOBILE_MEDIA = '(max-width: 720px)';

function RoomPicture({ room, className, objectPosition, priority }) {
  const desktop = ROOM_SRC_DESKTOP[room] || ROOM_SRC_DESKTOP.home;
  const mobileReady = Boolean(MOBILE_PLATES_READY[room]);
  const mobile = ROOM_SRC_MOBILE[room];

  return (
    <picture className={className === 'room-backdrop__fill' ? 'room-backdrop__fill-picture' : undefined}>
      {mobileReady && mobile ? <source media={MOBILE_MEDIA} srcSet={mobile} /> : null}
      <img
        className={className}
        src={desktop}
        alt=""
        draggable={false}
        decoding={priority ? 'sync' : 'async'}
        fetchPriority={priority ? 'high' : 'low'}
        style={objectPosition ? { objectPosition } : undefined}
        aria-hidden
      />
    </picture>
  );
}

/**
 * Full-bleed / contain room plate with light parallax — P5 Tech pattern.
 * Desktop: contain shows the whole plate + soft blurred edge fill (default).
 * Mobile: soft contain until a real 9:16 plate is marked READY, then cover.
 */
export function RoomBackdrop({
  room,
  dim = 0,
  fit = 'contain',
  parallax = true,
  position = 'center center',
}) {
  const layerRef = useRef(null);
  const key = ROOM_SRC_DESKTOP[room] ? room : 'home';
  const mobilePlate = Boolean(MOBILE_PLATES_READY[key]);

  useEffect(() => {
    const el = layerRef.current;
    if (!el) return;
    el.style.transform = '';
    if (!parallax) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 5;
      const y = (e.clientY / window.innerHeight - 0.5) * 3.5;
      el.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, [key, parallax]);

  return (
    <div
      className="room-backdrop"
      aria-hidden
      data-room={key}
      data-fit={fit}
      data-mobile-plate={mobilePlate ? 'true' : 'false'}
    >
      {fit === 'contain' ? <RoomPicture room={key} className="room-backdrop__fill" /> : null}
      <div ref={layerRef} className="room-backdrop__layer">
        <RoomPicture
          room={key}
          className="room-backdrop__img"
          objectPosition={fit === 'cover' ? position : undefined}
          priority
        />
      </div>
      {dim > 0 ? (
        <div
          className="room-backdrop__veil"
          style={{
            background: `linear-gradient(
              180deg,
              rgba(255, 252, 248, ${Math.min(dim * 0.9, 0.22)}) 0%,
              rgba(255, 252, 248, ${dim * 0.2}) 45%,
              rgba(255, 252, 248, ${Math.min(dim * 0.7, 0.18)}) 100%
            )`,
          }}
        />
      ) : null}
    </div>
  );
}
